import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '@/components/shared/Card';
import Button from '@/components/shared/Button';
import { apiFetch } from '@/lib/api';
import {
  FiSend, FiEye, FiCheckCircle, FiFileText, FiCopy, FiExternalLink,
  FiRefreshCw, FiZap, FiTrash2, FiCheck, FiChevronDown, FiChevronRight,
  FiMessageSquare, FiEdit3, FiDownload,
} from 'react-icons/fi';

/**
 * Angebotsuebersicht: alle per Link geteilten Kalkulationen mit ihrem echten
 * Status vom Server (gesendet -> angesehen -> beauftragt -> Vollmacht).
 */

// Status-Ableitung aus den Server-Feldern
const deriveStatus = (o) => {
  if (o.vollmacht_signed_at) return 'vollmacht';
  if (o.accepted_info) return 'beauftragt';
  if (Number(o.views) > 0) return 'angesehen';
  return 'gesendet';
};

const STATUS = {
  gesendet:   { label: 'Gesendet',   cls: 'bg-gray-100 text-gray-700',    icon: FiSend },
  angesehen:  { label: 'Angesehen',  cls: 'bg-blue-100 text-blue-700',    icon: FiEye },
  beauftragt: { label: 'Beauftragt', cls: 'bg-amber-100 text-amber-800',  icon: FiCheckCircle },
  vollmacht:  { label: 'Vollmacht',  cls: 'bg-green-100 text-green-700',  icon: FiFileText },
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('de-DE') : '–');
const fmtNum = (n) => (n == null || n === '' ? '–' : Number(n).toLocaleString('de-DE'));

// Preisspanne aus cheapestPerLaufzeit
/**
 * Preisspanne — MUSS dieselbe Groesse zeigen wie Kundenansicht und PDF.
 * Neu: Angebotspreis (Energie + Provision). Alte Angebote kannten das Feld
 * nicht; dort war der Komplettpreis der Angebotspreis und bleibt es auch.
 */
const preisVonLaufzeit = (p) => {
  if (!p) return null;
  if (typeof p.angebotspreis_ct_kwh === 'number') return p.angebotspreis_ct_kwh;
  if (typeof p.energie_ct_kwh === 'number') {
    return p.energie_ct_kwh + (typeof p.provision_ct_kwh === 'number' ? p.provision_ct_kwh : 0);
  }
  if (typeof p.komplettpreis_ct_kwh === 'number') return p.komplettpreis_ct_kwh;
  return null;
};

const fmtDateTime = (d) => (d
  ? new Date(d).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '–');

// Number(null) und Number('') sind 0 — ohne Vorpruefung wuerde aus einem
// fehlenden Preis im Beauftragungsprotokoll eine erfundene "0,00 ct/kWh".
const fmtCt = (v) => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n)
    ? `${n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ct/kWh`
    : null;
};

/**
 * accepted_info und vollmacht sind JSONB, die ein AUTH-FREIER Endpoint mit
 * beliebigem Inhalt fuellt. Ein verschachteltes Objekt als Feldwert wuerde React
 * beim Rendern werfen ("Objects are not valid as a React child") und damit die
 * interne Angebotsliste weissen Bildschirm zeigen lassen. Deshalb kommt hier
 * nur heraus, was sich sicher darstellen laesst.
 */
const txt = (v) => {
  if (v == null || v === '') return null;
  if (typeof v === 'string') return v.trim() || null;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return null;
};

// "12 Monate (1 Jahre)" liest sich falsch; bei krummen Laufzeiten bleibt es
// bei der Monatsangabe, statt eine Jahreszahl zu runden.
const laufzeitText = (monate) => {
  const jahre = monate / 12;
  if (!Number.isInteger(jahre)) return `${monate} Monate`;
  return `${monate} Monate (${jahre} ${jahre === 1 ? 'Jahr' : 'Jahre'})`;
};

const VOLLMACHT_UMFANG = {
  komplett: 'Kompletter Wechselservice',
  daten: 'Nur Datenerhebung',
};

const Feld = ({ label, children }) => (
  <div>
    <dt className="text-xs text-gray-500">{label}</dt>
    <dd className="text-sm text-gray-900">{children || '–'}</dd>
  </div>
);

/**
 * Was der Kunde zurueckgemeldet hat.
 *
 * Bis hierhin wurden accepted_info und vollmacht ausschliesslich zur
 * Statusableitung benutzt — die Anmerkung des Kunden war nirgends lesbar und
 * ging damit verloren. Genau dafuer ist dieses Panel da.
 */
const KundenRueckmeldung = ({ o }) => {
  const info = o.accepted_info && typeof o.accepted_info === 'object' ? o.accepted_info : null;
  const v = o.vollmacht && typeof o.vollmacht === 'object' ? o.vollmacht : null;
  const monate = Number(info?.preferred_laufzeit_monate);
  const laufzeit = Number.isFinite(monate) && monate > 0 ? laufzeitText(monate) : null;
  const von = fmtCt(info?.preis_min_ct_kwh);
  const bis = fmtCt(info?.preis_max_ct_kwh);
  const spanne = (von && bis) ? (von === bis ? von : `${von.replace(' ct/kWh', '')}–${bis}`) : null;
  const gewaehlt = fmtCt(info?.preferred_angebotspreis_ct_kwh ?? info?.preferred_komplettpreis_ct_kwh);
  const anmerkung = txt(info?.note);
  const anschrift = [txt(v?.strasse), txt(v?.plz_ort)].filter(Boolean).join(', ');
  const basis = info?.preisbasis === 'komplett' ? 'Komplettpreis (Altangebot)'
    : info?.preisbasis === 'energie' ? 'Energiepreis' : null;
  // Kein Default: fehlt die Angabe, steht '–' statt einer erfundenen Auskunft
  // ueber ein rechtlich relevantes Dokument.
  const kundentyp = v?.kundentyp === 'privat' ? 'Privat' : v?.kundentyp === 'gewerbe' ? 'Gewerbe' : null;
  // signature selbst kommt nicht mit (base64-Bild, siehe LIST-Endpoint) —
  // der Server meldet stattdessen hat_signatur.
  const unterschrift = txt(v?.typed_name) ? `getippt: ${txt(v.typed_name)}`
    : (o.hat_signatur ? 'gezeichnet' : null);

  if (!info && !v) {
    return (
      <p className="text-sm text-gray-500">
        Der Kunde hat sich zu diesem Angebot noch nicht zurueckgemeldet.
      </p>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {info && (
        <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-amber-800">
            <FiCheckCircle size={13} /> Beauftragung
          </p>
          <dl className="mt-3 grid grid-cols-2 gap-3">
            <Feld label="Bestaetigt am">{fmtDateTime(o.accepted_at || info.bestaetigt_am)}</Feld>
            <Feld label="Gewaehlte Laufzeit">{laufzeit}</Feld>
            <Feld label="Bestaetigte Spanne">{spanne}</Feld>
            <Feld label="Preis der Laufzeit">{gewaehlt}</Feld>
            <Feld label="Preisbasis">{basis}</Feld>
          </dl>
          <div className="mt-3 rounded border border-amber-200 bg-white p-3">
            <p className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
              <FiMessageSquare size={12} /> Anmerkung des Kunden
            </p>
            {anmerkung ? (
              <p className="mt-1 whitespace-pre-wrap text-sm text-gray-900">{anmerkung}</p>
            ) : (
              <p className="mt-1 text-sm text-gray-400">keine</p>
            )}
          </div>
        </div>
      )}

      {v && (
        <div className="rounded-lg border border-green-200 bg-green-50/60 p-4">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-green-800">
            <FiEdit3 size={13} /> Vollmacht
          </p>
          <dl className="mt-3 grid grid-cols-2 gap-3">
            <Feld label="Unterschrieben am">{fmtDateTime(o.vollmacht_signed_at || txt(v.signed_at))}</Feld>
            <Feld label="Umfang">{VOLLMACHT_UMFANG[v.vollmacht_type] || txt(v.vollmacht_type)}</Feld>
            <Feld label="Firma">{txt(v.firma)}</Feld>
            <Feld label="Unterzeichner">{txt(v.name)}</Feld>
            <Feld label="Anschrift">{anschrift}</Feld>
            <Feld label="Zaehlernummer">{txt(v.zaehlernummer)}</Feld>
            <Feld label="Kundentyp">{kundentyp}</Feld>
            <Feld label="Unterschrift">{unterschrift}</Feld>
          </dl>
          <a
            href={`/api/shared-kalkulationen/${o.slug}/vollmacht-pdf`}
            target="_blank"
            rel="noreferrer"
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-green-700 hover:text-green-800"
          >
            <FiDownload size={13} /> Vollmacht als PDF
          </a>
        </div>
      )}

      {info && !v && (
        <div className="rounded-lg border border-dashed border-gray-300 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Vollmacht</p>
          <p className="mt-2 text-sm text-gray-600">
            Noch nicht erteilt. Der Kunde hat die Preisspanne bestaetigt, aber den letzten Schritt nicht
            abgeschlossen — hier lohnt ein Anruf.
          </p>
        </div>
      )}
    </div>
  );
};

const priceRange = (preise) => {
  if (!preise || typeof preise !== 'object') return null;
  const vals = Object.values(preise)
    .map(preisVonLaufzeit)
    .filter((v) => typeof v === 'number');
  if (!vals.length) return null;
  const min = Math.min(...vals), max = Math.max(...vals);
  const f = (n) => n.toFixed(2).replace('.', ',');
  return min === max ? `${f(min)} ct` : `${f(min)}–${f(max)} ct`;
};

const OffersList = () => {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('alle');
  const [copied, setCopied] = useState('');
  // Aufgeklappte Zeile (Slug) — zeigt die Rueckmeldung des Kunden.
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await apiFetch('/api/shared-kalkulationen');
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Laden fehlgeschlagen');
      setOffers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const withStatus = useMemo(
    () => offers.map((o) => ({ ...o, _status: deriveStatus(o) })),
    [offers]
  );

  const stats = useMemo(() => {
    const s = { gesamt: withStatus.length, angesehen: 0, beauftragt: 0, vollmacht: 0 };
    withStatus.forEach((o) => {
      if (o._status === 'angesehen') s.angesehen++;
      if (o._status === 'beauftragt') s.beauftragt++;
      if (o._status === 'vollmacht') s.vollmacht++;
    });
    return s;
  }, [withStatus]);

  const visible = filter === 'alle' ? withStatus : withStatus.filter((o) => o._status === filter);

  const copyLink = (slug) => {
    navigator.clipboard.writeText(`${window.location.origin}/k/${slug}`).then(() => {
      setCopied(slug);
      setTimeout(() => setCopied(''), 2000);
    });
  };

  const remove = async (slug) => {
    if (!window.confirm('Dieses Angebot zurueckziehen? Der Kundenlink funktioniert danach nicht mehr.')) return;
    try {
      const resp = await apiFetch(`/api/shared-kalkulationen/${slug}`, { method: 'DELETE' });
      if (resp.ok) setOffers((prev) => prev.filter((o) => o.slug !== slug));
    } catch { /* ignore */ }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Angebote</h1>
          <p className="text-gray-500 mt-1">
            {loading ? 'Lade …' : `${stats.gesamt} versendete Angebote`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" icon={FiRefreshCw} onClick={load}>Aktualisieren</Button>
          <Button icon={FiZap} onClick={() => navigate('/app/kalkulator')}>Neues Angebot</Button>
        </div>
      </div>

      {/* Kennzahlen = zugleich Filter */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { key: 'alle', label: 'Gesamt', value: stats.gesamt, icon: FiSend },
          { key: 'angesehen', label: 'Angesehen', value: stats.angesehen, icon: FiEye },
          { key: 'beauftragt', label: 'Beauftragt', value: stats.beauftragt, icon: FiCheckCircle },
          { key: 'vollmacht', label: 'Vollmacht erteilt', value: stats.vollmacht, icon: FiFileText },
        ].map((k) => (
          <button
            key={k.key}
            onClick={() => setFilter(k.key)}
            className={`text-left rounded-xl border p-4 transition-colors ${
              filter === k.key ? 'border-primary-500 bg-primary-50' : 'border-gray-200 bg-white hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center gap-2 text-gray-500">
              <k.icon size={15} />
              <span className="text-sm">{k.label}</span>
            </div>
            <p className="text-2xl font-bold mt-1 text-gray-900">{k.value}</p>
          </button>
        ))}
      </div>

      {error && (
        <Card className="bg-red-50 border-red-200">
          <p className="text-sm text-red-700">Angebote konnten nicht geladen werden: {error}</p>
        </Card>
      )}

      <Card padding={false}>
        <div className="overflow-x-auto">
          {loading ? (
            <p className="text-sm text-gray-400 py-12 text-center">Lade Angebote …</p>
          ) : visible.length === 0 ? (
            <div className="py-12 text-center">
              <FiFileText className="mx-auto text-gray-300 mb-3" size={32} />
              <p className="text-gray-600 font-medium">
                {stats.gesamt === 0 ? 'Noch keine Angebote versendet' : 'Keine Angebote in dieser Ansicht'}
              </p>
              {stats.gesamt === 0 && (
                <Button className="mt-3" icon={FiZap} onClick={() => navigate('/app/kalkulator')}>
                  Erstes Angebot kalkulieren
                </Button>
              )}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500 bg-gray-50">
                  <th className="px-4 py-3">Kunde</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Verbrauch</th>
                  <th className="px-4 py-3 text-right">Preisspanne (Energie)</th>
                  <th className="px-4 py-3 text-right">Aufrufe</th>
                  <th className="px-4 py-3">Gesendet</th>
                  <th className="px-4 py-3">Gueltig bis</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {visible.map((o) => {
                  const st = STATUS[o._status];
                  const range = priceRange(o.preise);
                  // Aufklappbar ist nur, wo es auch etwas zu lesen gibt.
                  const hatRueckmeldung = Boolean(o.accepted_info || o.vollmacht_signed_at);
                  const offen = expanded === o.slug;
                  return (
                    <React.Fragment key={o.slug}>
                    <tr
                      className={`border-t border-gray-100 ${hatRueckmeldung ? 'cursor-pointer' : ''} ${offen ? 'bg-gray-50' : 'hover:bg-gray-50'}`}
                      onClick={hatRueckmeldung ? () => setExpanded(offen ? null : o.slug) : undefined}
                      tabIndex={hatRueckmeldung ? 0 : undefined}
                      aria-expanded={hatRueckmeldung ? offen : undefined}
                      onKeyDown={hatRueckmeldung ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setExpanded(offen ? null : o.slug);
                        }
                      } : undefined}
                    >
                      <td className="px-4 py-3">
                        <p className="flex items-center gap-1.5 font-medium text-gray-900">
                          {hatRueckmeldung && (
                            offen ? <FiChevronDown size={13} className="text-gray-400" />
                                  : <FiChevronRight size={13} className="text-gray-400" />
                          )}
                          {o.company_name || o.contact_name || '–'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {o.contact_name && o.company_name ? o.contact_name : ''}
                          {o.plz ? ` · ${o.plz}` : ''}
                          {o.zaehlverfahren ? ` · ${o.zaehlverfahren}` : ''}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>
                          <st.icon size={11} /> {st.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                        {fmtNum(o.verbrauch)} kWh
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-700">{range || '–'}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-700">{o.views ?? 0}</td>
                      <td className="px-4 py-3 text-gray-600">{fmtDate(o.created_at)}</td>
                      <td className="px-4 py-3 text-gray-600">{fmtDate(o.valid_until)}</td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => copyLink(o.slug)}
                            title="Kundenlink kopieren"
                            className="p-1.5 rounded hover:bg-gray-200 text-gray-500 hover:text-gray-800"
                          >
                            {copied === o.slug ? <FiCheck size={14} className="text-green-600" /> : <FiCopy size={14} />}
                          </button>
                          <a
                            /* ?preview=1: der eigene Kontrollblick soll den Lead
                               nicht auf "Kunde hat geoeffnet" setzen. */
                            href={`/k/${o.slug}?preview=1`}
                            target="_blank"
                            rel="noreferrer"
                            title="Angebot oeffnen (Vorschau, zaehlt nicht als Kundenaufruf)"
                            className="p-1.5 rounded hover:bg-gray-200 text-gray-500 hover:text-gray-800 inline-flex"
                          >
                            <FiExternalLink size={14} />
                          </a>
                          <a
                            href={`/api/shared-kalkulationen/${o.slug}/pdf`}
                            target="_blank"
                            rel="noreferrer"
                            title="PDF ansehen"
                            className="p-1.5 rounded hover:bg-gray-200 text-gray-500 hover:text-gray-800 inline-flex"
                          >
                            <FiFileText size={14} />
                          </a>
                          <button
                            onClick={() => remove(o.slug)}
                            title="Angebot zurueckziehen"
                            className="p-1.5 rounded hover:bg-red-100 text-gray-400 hover:text-red-600"
                          >
                            <FiTrash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {offen && (
                      <tr className="bg-gray-50">
                        <td colSpan={8} className="px-4 pb-5 pt-1">
                          <KundenRueckmeldung o={o} />
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </Card>
    </div>
  );
};

export default OffersList;
